import { useCallback, useState } from "react";
import { toast } from "sonner";
import { API_BASE, getToken } from "@/lib/api";
import { readSSEStream } from "@/lib/sse";

let msgSeq = 0;
const newMsg = (role, content) => ({ id: `m${++msgSeq}`, role, content });

const updateLast = (setMessages, fn) =>
  setMessages((prev) => {
    const next = [...prev];
    const last = next[next.length - 1];
    if (last) next[next.length - 1] = fn(last);
    return next;
  });

function handleEvent({ event, data }, setSessionId, setMessages) {
  if (event === "session") return setSessionId(data);
  if (event === "done") return;
  let parsed;
  try {
    parsed = JSON.parse(data);
  } catch (err) {
    console.error("Malformed AI stream event", err);
    if (event === "error") toast.error("AI error");
    return;
  }
  if (event === "error") return toast.error(parsed.message || "AI error");
  if (parsed.delta) updateLast(setMessages, (m) => ({ ...m, content: m.content + parsed.delta }));
}

export function useCoachChat() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);

  const send = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    setMessages((prev) => [...prev, newMsg("user", text), newMsg("assistant", "")]);
    setStreaming(true);
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      await readSSEStream(resp, (evt) => handleEvent(evt, setSessionId, setMessages));
    } catch (err) {
      console.error("AI request failed", err);
      toast.error("AI request failed");
      updateLast(setMessages, (m) =>
        m.role === "assistant" && !m.content ? { ...m, content: "(request failed — please try again)" } : m);
    } finally {
      setStreaming(false);
    }
  }, [streaming, sessionId]);

  return { messages, streaming, send };
}
